<template>
  <div
    v-show="screenReady"
    class="error-notification">
    <div class="error-notification__contain">
      <div class="error-notification__header">
        {{ lastItem.title || 'An error has occurred' }}
      </div>
      <div class="error-notification__details">
        <div
          v-for="(value, key) in parsedErrorDetails"
          :key="key"
          class="error-notification__detail-row">
          <span class="error-notification__detail-label">{{ key }}:</span>
          <span class="error-notification__detail-value">{{ value }}</span>
        </div>
      </div>
      <div class="error-notification__actions">
        <button
          class="error-notification__action-btn"
          @click="copyErrorToClipboard">
          <span class="error-notification__action-icon shape-copy--admin" />
          Copy details
        </button>
        <button
          class="error-notification__action-btn"
          @click="captureScreenshot">
          Take screenshot
        </button>
      </div>
      <button
        class="error-notification__close-button shape-cross"
        @click="hideNotification" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErrorItemBooster',

  props: {
    notification: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      screenReady: true,
    };
  },

  computed: {
    lastItem() {
      return this.notification.items[this.notification.items.length - 1];
    },

    parsedErrorDetails() {
      const message = this.lastItem.message || '';
      const details = {};

      const lines = message.split('\n').map((line) => line.trim());

      lines.forEach((line) => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          const key = line.substring(0, colonIndex).trim();
          const value = line.substring(colonIndex + 1).trim();
          details[key] = value;
        }
      });

      return details;
    },

    fullErrorText() {
      // eslint-disable-next-line max-len
      return `
Request:
  ${this.lastItem.message};

Meta:
  Page: ${window.location.href};
  User Agent: ${navigator.userAgent}
  User: ${this.$store.state.user.email}`;
    },
  },

  methods: {
    hideNotification() {
      this.$emit('hide', this.notification.id);
    },

    copyErrorToClipboard() {
      navigator.clipboard.writeText(this.fullErrorText);
    },

    async captureScreenshot() {
      try {
        this.screenReady = false;
        const stream = await navigator.mediaDevices.getDisplayMedia({
          preferCurrentTab: true,
          video: {
            mediaSource: 'screen',
          },
        });

        const video = document.createElement('video');
        video.srcObject = stream;

        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            video.play();
            resolve();
          };
        });

        const errorInfoHeight = 100 + (this.fullErrorText.split('\n').length * 30);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight + errorInfoHeight;

        ctx.fillStyle = 'rgb(0, 0, 0)';
        ctx.fillRect(0, 0, canvas.width, errorInfoHeight);

        ctx.drawImage(video, 0, errorInfoHeight, video.videoWidth, video.videoHeight);

        ctx.font = 'bold 30px Arial';
        ctx.fillStyle = 'white';
        ctx.fillText(`Error captured at: ${new Date().toISOString()}`, 20, 40);
        ctx.fillText(`Page: ${window.location.href}`, 20, 70);

        ctx.font = '25px Arial';
        let lineY = 110;
        this.fullErrorText.split('\n').forEach((string) => {
          ctx.fillText(string, 20, lineY);
          lineY += 30;
        });

        stream.getTracks().forEach((track) => track.stop());

        canvas.toBlob(async (blob) => {
          try {
            const item = new window.ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
          } catch (err) {
            console.error('Failed to copy screenshot to clipboard:', err);
            // Fallback: download the image
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `error-screenshot-${Date.now()}.png`;
            a.click();
            URL.revokeObjectURL(url);
          }
        });
      } catch (err) {
        console.error('Error capturing screenshot:', err);
      } finally {
        this.screenReady = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.error-notification {
  display: flex;
  position: relative;
  overflow: hidden;

  &:before, &:after {
    content: '';
    mask: url('/images/icons/alert.svg') no-repeat center;
    -webkit-mask: url('/images/icons/alert.svg') no-repeat center;
    mask-size: cover;
    -webkit-mask-size: cover;
    width: 165px;
    height: 153px;
    position: absolute;
    background-color:  rgba($color--basic-white, .2);
  }

  &:before {
    left: 58px;
    top: -28px;
  }

  &:after {
    right: 112px;
    top: -76px;
  }
}

.error-notification__contain {
  margin: auto;
  padding: 20px;
}

.error-notification__header {
  font-weight: 500;
  font-size: 19px;
  line-height: 23px;
  text-align: center;
  margin-bottom: 15px;
  color: $color--basic-white;
}

.error-notification__details {
  background-color: rgba($color--basic-black, 0.2);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: auto;
}

.error-notification__detail-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
}

.error-notification__detail-label {
  font-weight: 500;
  min-width: 100px;
  color: $color--basic-light-gray;
}

.error-notification__detail-value {
  flex: 1;
  word-break: break-word;
  color: $color--basic-white;
}

.error-notification__actions {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.error-notification__action-btn {
  display: flex;
  align-items: center;
  background-color: rgba($color--basic-white, 0.15);
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  color: $color--basic-white;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba($color--basic-white, 0.25);
  }
}

.error-notification__action-icon {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

.error-notification__close-button {
  position: absolute;
  width: 22px;
  height: 22px;
  background-color: transparent;
  border: none;
  right: 15px;
  top: 15px;
  cursor: pointer;
}
</style>
